import type { ComponentType } from '@/types/schemas/workspace'
import type { PanelComponent, PanelProps } from './types'
import { lazyPanel } from '@/components/common/lazy-panel'
import { EDGESCRIPT_ENABLED } from '@/features/edgescript/flags'
import { REALTIME_WEBSOCKET_ENABLED } from '@/services/realtime/flags'
import { LazyScriptEditorPanel } from '@/features/edgescript/panel/lazy-script-editor-panel'
import { DEV_FLAGS, FLAGS } from '@/services/analytics/feature-flags'
import { RolloutGate } from './components/rollout-gate'
import { DevFlagGate } from '@/components/common/dev-flag-gate'

// ─── Lazy-loaded dashboard panels ───

const SyncedChartGrid = lazyPanel(
  () => import('@/features/charts/synced-chart-grid'),
  'SyncedChartGrid',
)
const ScannerPanel = lazyPanel(() => import('@/features/scanner/scanner-panel'), 'ScannerPanel')
const KeyStatsPanel = lazyPanel(
  () => import('@/features/key-stats/key-stats-panel'),
  'KeyStatsPanel',
)
const WatchlistPanel = lazyPanel(
  () => import('@/features/watchlist/watchlist-panel'),
  'WatchlistPanel',
)
const NewsPanel = lazyPanel(() => import('@/features/news/news-panel'), 'NewsPanel')
const TickerStatsPanel = lazyPanel(
  () => import('@/features/ticker-stats/ticker-stats-panel'),
  'TickerStatsPanel',
)
const CalendarPanel = lazyPanel(() => import('@/features/calendar/calendar-panel'), 'CalendarPanel')
const MultiScannerPanel = lazyPanel(
  () => import('@/features/multi-scanner/multi-scanner-panel'),
  'MultiScannerPanel',
)
const LazyNotebookPanel = lazyPanel(
  () => import('@/features/notebook/components/notebook-panel'),
  'NotebookPanel',
)
/** A Notebook saved into a layout still answers to `notebook-release`. */
function NotebookPanel(props: PanelProps) {
  return (
    <RolloutGate flag={FLAGS.NOTEBOOK}>
      <LazyNotebookPanel {...props} />
    </RolloutGate>
  )
}
const AlertLogPanel = lazyPanel(
  () => import('@/features/scanner/alerts/alert-log-panel'),
  'AlertLogPanel',
)
const LazyPriceAlertsPanel = lazyPanel(
  () => import('@/features/price-alerts/price-alerts-panel'),
  'PriceAlertsPanel',
)
const LazyAlertWindowsPanel = lazyPanel(
  () => import('@/features/alert-windows/alert-windows-panel'),
  'AlertWindowsPanel',
)
/** Saved Price Alerts / Alert Windows panels answer to their release flags, inside the realtime build gate. */
function PriceAlertsPanel(props: PanelProps) {
  return (
    <RolloutGate flag={FLAGS.PRICE_ALERTS}>
      <LazyPriceAlertsPanel {...props} />
    </RolloutGate>
  )
}
function AlertWindowsPanel(props: PanelProps) {
  return (
    <RolloutGate flag={FLAGS.ALERT_WINDOWS}>
      <LazyAlertWindowsPanel {...props} />
    </RolloutGate>
  )
}
const MultiChartPanel = lazyPanel(
  () => import('@/features/multi-chart/multi-chart-panel'),
  'MultiChartPanel',
)
const HeatmapPanel = lazyPanel(() => import('@/features/heatmap/heatmap-panel'), 'HeatmapPanel')
const ThemeTrackerPanel = lazyPanel(
  () => import('@/features/theme-tracker/theme-tracker-panel'),
  'ThemeTrackerPanel',
)
const LazyMarketOverwatchPanel = lazyPanel(
  () => import('@/features/market-overwatch/market-overwatch-panel'),
  'MarketOverwatchPanel',
)
/** A Market Overwatch saved into a layout answers to `home-release`, like the Home view. */
function MarketOverwatchPanel(props: PanelProps) {
  return (
    <RolloutGate flag={FLAGS.HOME}>
      <LazyMarketOverwatchPanel {...props} />
    </RolloutGate>
  )
}
/**
 * A Script Editor saved into a layout answers to `edgescript-release` inside the build
 * gate (registered below only when `EDGESCRIPT_ENABLED`). The panel and its state stay.
 */
function ScriptEditorPanel(props: PanelProps) {
  return (
    <RolloutGate flag={FLAGS.EDGESCRIPT}>
      <LazyScriptEditorPanel {...props} />
    </RolloutGate>
  )
}

const LazyScannerParityPanel = lazyPanel(
  () => import('@/features/scanner-parity/scanner-parity-panel'),
  'ScannerParityPanel',
)
/** Scanner Parity (dev tool) answers to the PostHog dev flag devtools-scanner-parity. */
function ScannerParityPanel(props: PanelProps) {
  return (
    <DevFlagGate flag={DEV_FLAGS.SCANNER_PARITY}>
      <LazyScannerParityPanel {...props} />
    </DevFlagGate>
  )
}

// ─── Lazy-loaded history panels ───

const HistoryRecordsPanel = lazyPanel(
  () => import('@/features/history/components/history-records-panel'),
  'HistoryRecordsPanel',
)
const HistoryStatsPanel = lazyPanel(
  () => import('@/features/history/components/history-stats-panel'),
  'HistoryStatsPanel',
)
const HistoryTimeSeriesPanel = lazyPanel(
  () => import('@/features/history/components/history-time-series-panel'),
  'HistoryTimeSeriesPanel',
)
const HistoryScatterPanel = lazyPanel(
  () => import('@/features/history/components/history-scatter-panel'),
  'HistoryScatterPanel',
)
const HistoryBarPanel = lazyPanel(
  () => import('@/features/history/components/history-bar-panel'),
  'HistoryBarPanel',
)
const HistoryBarCategoryPanel = lazyPanel(
  () => import('@/features/history/components/history-bar-category-panel'),
  'HistoryBarCategoryPanel',
)
const HistoryDonutCategoryPanel = lazyPanel(
  () => import('@/features/history/components/history-donut-category-panel'),
  'HistoryDonutCategoryPanel',
)
const HistoryChartPanel = lazyPanel(
  () => import('@/features/history/components/history-chart-panel'),
  'HistoryChartPanel',
)
const HistoryNewsPanel = lazyPanel(
  () => import('@/features/history/components/history-news-panel'),
  'HistoryNewsPanel',
)
const HistoryComparisonPanel = lazyPanel(
  () => import('@/features/history/components/history-comparison-panel'),
  'HistoryComparisonPanel',
)
const HistoryIntradayPanel = lazyPanel(
  () => import('@/features/history/components/history-intraday-panel'),
  'HistoryIntradayPanel',
)
const HistoryCalendarPanel = lazyPanel(
  () => import('@/features/history/components/history-calendar-panel'),
  'HistoryCalendarPanel',
)
const HistoryChartGridPanel = lazyPanel(
  () => import('@/features/history/components/history-chart-grid-panel'),
  'HistoryChartGridPanel',
)
const HistorySankeyPanel = lazyPanel(
  () => import('@/features/history/components/history-sankey-panel'),
  'HistorySankeyPanel',
)
const HistoryScorecardPanel = lazyPanel(
  () => import('@/features/history/components/scorecard-panel'),
  'ScorecardPanel',
)
const HistoryHeatmapPanel = lazyPanel(
  () => import('@/features/history/heatmap/history-heatmap-panel'),
  'HistoryHeatmapPanel',
)
const TimeDistributionPanel = lazyPanel(
  () => import('@/features/history/time-distribution/time-distribution-panel'),
  'TimeDistributionPanel',
)
const TagExplorerPanel = lazyPanel(
  () => import('@/features/tags/panels/tag-explorer-panel'),
  'TagExplorerPanel',
)

// ─── Lazy-loaded backtest report panels ───

// 'backtest-metrics' is the Key Metrics strip (the five-tile row). The
// strategy Report page renders it above its mosaic; in Run mode it is the top
// row of the Overview preset, so it is a real panel here.
const BacktestMetricsPanel = lazyPanel(
  () => import('@/features/backtest/report/panels/backtest-metrics-panel'),
  'BacktestMetricsPanel',
)
const BacktestOverviewPanel = lazyPanel(
  () => import('@/features/backtest/report/panels/backtest-overview-panel'),
  'BacktestOverviewPanel',
)
const BacktestPerformancePanel = lazyPanel(
  () => import('@/features/backtest/report/panels/backtest-performance-panel'),
  'BacktestPerformancePanel',
)
const BacktestTradesPanel = lazyPanel(
  () => import('@/features/backtest/report/panels/backtest-trades-panel'),
  'BacktestTradesPanel',
)
const BacktestCalendarPanel = lazyPanel(
  () => import('@/features/backtest/report/panels/backtest-calendar-panel'),
  'BacktestCalendarPanel',
)
const BacktestDistributionsPanel = lazyPanel(
  () => import('@/features/backtest/report/panels/backtest-distributions-panel'),
  'BacktestDistributionsPanel',
)
const BacktestBucketsPanel = lazyPanel(
  () => import('@/features/backtest/report/panels/backtest-buckets-panel'),
  'BacktestBucketsPanel',
)
const BacktestSymbolsPanel = lazyPanel(
  () => import('@/features/backtest/report/panels/backtest-symbols-panel'),
  'BacktestSymbolsPanel',
)
const BacktestRiskPanel = lazyPanel(
  () => import('@/features/backtest/report/panels/backtest-risk-panel'),
  'BacktestRiskPanel',
)
const BacktestWinsLossesPanel = lazyPanel(
  () => import('@/features/backtest/report/panels/backtest-wins-losses-panel'),
  'BacktestWinsLossesPanel',
)
const BacktestRunTradesPanel = lazyPanel(
  () => import('@/features/backtest/report/panels/backtest-run-trades-panel'),
  'BacktestRunTradesPanel',
)
const BacktestChartGridPanel = lazyPanel(
  () => import('@/features/backtest/report/panels/backtest-chart-grid-panel'),
  'BacktestChartGridPanel',
)
const BacktestFilterLabPanel = lazyPanel(
  () => import('@/features/backtest/report/panels/backtest-filter-lab-panel'),
  'BacktestFilterLabPanel',
)
const BacktestFieldExplorerPanel = lazyPanel(
  () => import('@/features/backtest/report/panels/backtest-field-explorer-panel'),
  'BacktestFieldExplorerPanel',
)
const BacktestMonteCarloPanel = lazyPanel(
  () => import('@/features/backtest/report/panels/backtest-monte-carlo-panel'),
  'BacktestMonteCarloPanel',
)
const BacktestMonteCarloBandsPanel = lazyPanel(
  () => import('@/features/backtest/report/panels/backtest-monte-carlo-bands-panel'),
  'BacktestMonteCarloBandsPanel',
)
const BacktestIsOosPanel = lazyPanel(
  () => import('@/features/backtest/report/panels/backtest-is-oos-panel'),
  'BacktestIsOosPanel',
)
const BacktestPeriodStabilityPanel = lazyPanel(
  () => import('@/features/backtest/report/panels/backtest-period-stability-panel'),
  'BacktestPeriodStabilityPanel',
)
const BacktestBaselinePanel = lazyPanel(
  () => import('@/features/backtest/report/panels/backtest-baseline-panel'),
  'BacktestBaselinePanel',
)
const BacktestSizingSettingsPanel = lazyPanel(
  () => import('@/features/backtest/report/panels/backtest-sizing-settings-panel'),
  'BacktestSizingSettingsPanel',
)
const BacktestSizingEquityPanel = lazyPanel(
  () => import('@/features/backtest/report/panels/backtest-sizing-equity-panel'),
  'BacktestSizingEquityPanel',
)
const BacktestNotTakenPanel = lazyPanel(
  () => import('@/features/backtest/report/panels/backtest-not-taken-panel'),
  'BacktestNotTakenPanel',
)
const BacktestStreaksPanel = lazyPanel(
  () => import('@/features/backtest/report/panels/backtest-streaks-panel'),
  'BacktestStreaksPanel',
)
const BacktestTradeChartPanel = lazyPanel(
  () => import('@/features/backtest/report/panels/backtest-trade-chart-panel'),
  'BacktestTradeChartPanel',
)
const BacktestTradeFillsPanel = lazyPanel(
  () => import('@/features/backtest/report/panels/backtest-trade-fills-panel'),
  'BacktestTradeFillsPanel',
)
const BacktestDailyPnlPanel = lazyPanel(
  () => import('@/features/backtest/report/panels/backtest-daily-pnl-panel'),
  'BacktestDailyPnlPanel',
)
const BacktestWinRatePanel = lazyPanel(
  () => import('@/features/backtest/report/panels/backtest-win-rate-panel'),
  'BacktestWinRatePanel',
)
const BacktestMonthlyPanel = lazyPanel(
  () => import('@/features/backtest/report/panels/backtest-monthly-panel'),
  'BacktestMonthlyPanel',
)
const BacktestMonthlyTablePanel = lazyPanel(
  () => import('@/features/backtest/report/panels/backtest-monthly-table-panel'),
  'BacktestMonthlyTablePanel',
)
const BacktestHoldTimesPanel = lazyPanel(
  () => import('@/features/backtest/report/panels/backtest-hold-times-panel'),
  'BacktestHoldTimesPanel',
)
const BacktestDistHourPanel = lazyPanel(
  () => import('@/features/backtest/report/panels/backtest-distribution-panels'),
  'BacktestDistHourPanel',
)
const BacktestDistDayPanel = lazyPanel(
  () => import('@/features/backtest/report/panels/backtest-distribution-panels'),
  'BacktestDistDayPanel',
)
const BacktestDistDurationPanel = lazyPanel(
  () => import('@/features/backtest/report/panels/backtest-distribution-panels'),
  'BacktestDistDurationPanel',
)
const BacktestReturnHistogramPanel = lazyPanel(
  () => import('@/features/backtest/report/panels/backtest-return-histogram-panel'),
  'BacktestReturnHistogramPanel',
)
const BacktestDayMetricsPanel = lazyPanel(
  () => import('@/features/backtest/report/panels/backtest-day-metrics-panel'),
  'BacktestDayMetricsPanel',
)
const BacktestRollingWindowsPanel = lazyPanel(
  () => import('@/features/backtest/report/panels/backtest-rolling-windows-panel'),
  'BacktestRollingWindowsPanel',
)
const BacktestGeneralMetricsPanel = lazyPanel(
  () => import('@/features/backtest/report/panels/backtest-general-metrics-panel'),
  'BacktestGeneralMetricsPanel',
)
const BacktestEquityCurvePanel = lazyPanel(
  () => import('@/features/backtest/report/panels/backtest-equity-curve-panel'),
  'BacktestEquityCurvePanel',
)
const BacktestStrategyRollupPanel = lazyPanel(
  () => import('@/features/backtest/report/panels/backtest-strategy-rollup-panel'),
  'BacktestStrategyRollupPanel',
)

// ─── Journal panels (lazy: their chunk loads only on /journal) ───

const JournalEdgeRatingPanel = lazyPanel(
  () => import('@/features/journal/panels/journal-edge-rating-panel'),
  'JournalEdgeRatingPanel',
)
const JournalDisciplinePanel = lazyPanel(
  () => import('@/features/journal/panels/journal-discipline-panel'),
  'JournalDisciplinePanel',
)
const JournalCalendarPanel = lazyPanel(
  () => import('@/features/journal/panels/journal-calendar-panel'),
  'JournalCalendarPanel',
)
const JournalDailyPnlPanel = lazyPanel(
  () => import('@/features/journal/panels/journal-daily-pnl-panel'),
  'JournalDailyPnlPanel',
)
const JournalDayMetricsPanel = lazyPanel(
  () => import('@/features/journal/panels/journal-day-metrics-panel'),
  'JournalDayMetricsPanel',
)
const JournalDayTradesPanel = lazyPanel(
  () => import('@/features/journal/panels/journal-day-trades-panel'),
  'JournalDayTradesPanel',
)
const JournalDayPlansPanel = lazyPanel(
  () => import('@/features/journal/panels/journal-day-plans-panel'),
  'JournalDayPlansPanel',
)
const JournalDayPnlPanel = lazyPanel(
  () => import('@/features/journal/panels/journal-day-pnl-panel'),
  'JournalDayPnlPanel',
)
const JournalRecapPanel = lazyPanel(
  () => import('@/features/journal/panels/journal-recap-panel'),
  'JournalRecapPanel',
)
const JournalTradesPanel = lazyPanel(
  () => import('@/features/journal/panels/journal-trades-panel'),
  'JournalTradesPanel',
)
const JournalTradeDetailPanel = lazyPanel(
  () => import('@/features/journal/panels/journal-trade-detail-panel'),
  'JournalTradeDetailPanel',
)
const JournalAnnotationPanel = lazyPanel(
  () => import('@/features/journal/panels/journal-annotation-panel'),
  'JournalAnnotationPanel',
)
const JournalPlansPanel = lazyPanel(
  () => import('@/features/journal/panels/journal-plans-panel'),
  'JournalPlansPanel',
)
const JournalUntakenPlansPanel = lazyPanel(
  () => import('@/features/journal/panels/journal-untaken-plans-panel'),
  'JournalUntakenPlansPanel',
)
const JournalExitViewPanel = lazyPanel(
  () => import('@/features/journal/panels/journal-exit-view-panel'),
  'JournalExitViewPanel',
)
const JournalSetAndForgetPanel = lazyPanel(
  () => import('@/features/journal/panels/journal-set-and-forget-panel'),
  'JournalSetAndForgetPanel',
)
const JournalMistakesPanel = lazyPanel(
  () => import('@/features/journal/panels/journal-mistakes-panel'),
  'JournalMistakesPanel',
)
const JournalContextBreakdownPanel = lazyPanel(
  () => import('@/features/journal/panels/journal-context-breakdown-panel'),
  'JournalContextBreakdownPanel',
)
const JournalContextScatterPanel = lazyPanel(
  () => import('@/features/journal/panels/journal-context-scatter-panel'),
  'JournalContextScatterPanel',
)
const JournalPopulationPanel = lazyPanel(
  () => import('@/features/journal/panels/journal-population-panel'),
  'JournalPopulationPanel',
)
const JournalBacktestLivePanel = lazyPanel(
  () => import('@/features/journal/panels/journal-backtest-live-panel'),
  'JournalBacktestLivePanel',
)
const JournalLessonsPanel = lazyPanel(
  () => import('@/features/journal/panels/journal-lessons-panel'),
  'JournalLessonsPanel',
)
const JournalAskPanel = lazyPanel(
  () => import('@/features/journal/panels/journal-ask-panel'),
  'JournalAskPanel',
)

// ─── Placeholder panel ───

/**
 * Placeholder panel used when the actual panel component hasn't been built yet.
 * Shows a simple label with the component type.
 */
function PlaceholderPanel({
  panelId,
  windowConfig,
}: {
  panelId: string
  windowConfig: { componentType: ComponentType }
  symbols: string[]
}) {
  return (
    <div className="flex h-full w-full items-center justify-center text-muted-foreground">
      <div className="text-center">
        <p className="text-sm font-medium">{windowConfig.componentType}</p>
        <p className="text-xs">{panelId}</p>
      </div>
    </div>
  )
}

// ─── Registry factory ───

/**
 * Component registry — maps ComponentType to a React panel component.
 *
 * Different workspace pages use different registries:
 * - Dashboard: chart, scanner, key-stats, watchlist, news
 * - History: history-records, history-chart, history-stats, etc.
 *
 * Call `createComponentRegistry()` with overrides to register real components
 * as they are built. Unregistered types fall back to PlaceholderPanel.
 */
function createComponentRegistry(
  overrides: Partial<Record<ComponentType, PanelComponent>> = {},
): Record<ComponentType, PanelComponent> {
  return {
    chart: PlaceholderPanel,
    // 'bar-chart' kept in schema for graceful degradation of persisted layouts;
    // no real panel (removed — the scanner's inline 'bar' display mode supersedes
    // it). Falls back to PlaceholderPanel.
    'bar-chart': PlaceholderPanel,
    scanner: PlaceholderPanel,
    'multi-scanner': PlaceholderPanel,
    'key-stats': PlaceholderPanel,
    watchlist: PlaceholderPanel,
    news: PlaceholderPanel,
    'ticker-stats': PlaceholderPanel,
    calendar: PlaceholderPanel,
    'history-records': PlaceholderPanel,
    'history-chart': PlaceholderPanel,
    'history-stats': PlaceholderPanel,
    'history-news': PlaceholderPanel,
    'history-comparison': PlaceholderPanel,
    'history-time-series': PlaceholderPanel,
    'history-scatter': PlaceholderPanel,
    'history-bar': PlaceholderPanel,
    'history-bar-category': PlaceholderPanel,
    'history-donut-category': PlaceholderPanel,
    // 'history-deep-dive' retired (audit D11); kept for graceful degradation.
    'history-deep-dive': PlaceholderPanel,
    'history-intraday': PlaceholderPanel,
    'history-calendar': PlaceholderPanel,
    'history-multi-chart': PlaceholderPanel,
    'history-sankey': PlaceholderPanel,
    'history-scorecard': PlaceholderPanel,
    'history-heatmap': PlaceholderPanel,
    'history-time-distribution': PlaceholderPanel,
    // 'history-briefing' kept in schema for graceful degradation; no real panel.
    'history-briefing': PlaceholderPanel,
    'tags-explorer': PlaceholderPanel,
    notebook: PlaceholderPanel,
    'alert-log': PlaceholderPanel,
    'price-alerts': PlaceholderPanel,
    'alert-windows': PlaceholderPanel,
    'multi-chart': PlaceholderPanel,
    'sector-heatmap': PlaceholderPanel,
    'theme-tracker': PlaceholderPanel,
    'market-overwatch': PlaceholderPanel,
    'script-editor': PlaceholderPanel,
    'scanner-parity': PlaceholderPanel,
    'backtest-metrics': PlaceholderPanel,
    'backtest-overview': PlaceholderPanel,
    'backtest-performance': PlaceholderPanel,
    'backtest-trades': PlaceholderPanel,
    'backtest-calendar': PlaceholderPanel,
    'backtest-distributions': PlaceholderPanel,
    'backtest-buckets': PlaceholderPanel,
    'backtest-symbols': PlaceholderPanel,
    'backtest-risk': PlaceholderPanel,
    'backtest-wins-losses': PlaceholderPanel,
    'backtest-streaks': PlaceholderPanel,
    'backtest-trade-chart': PlaceholderPanel,
    'backtest-trade-fills': PlaceholderPanel,
    'backtest-daily-pnl': PlaceholderPanel,
    'backtest-win-rate': PlaceholderPanel,
    'backtest-monthly': PlaceholderPanel,
    'backtest-monthly-table': PlaceholderPanel,
    'backtest-hold-times': PlaceholderPanel,
    'backtest-dist-hour': PlaceholderPanel,
    'backtest-dist-day': PlaceholderPanel,
    'backtest-dist-duration': PlaceholderPanel,
    'backtest-return-histogram': PlaceholderPanel,
    'backtest-day-metrics': PlaceholderPanel,
    'backtest-rolling-windows': PlaceholderPanel,
    'backtest-general-metrics': PlaceholderPanel,
    'backtest-equity-curve': PlaceholderPanel,
    'backtest-strategy-rollup': PlaceholderPanel,
    'backtest-run-trades': PlaceholderPanel,
    'backtest-chart-grid': PlaceholderPanel,
    'backtest-filter-lab': PlaceholderPanel,
    'backtest-field-explorer': PlaceholderPanel,
    'backtest-monte-carlo': PlaceholderPanel,
    'backtest-monte-carlo-bands': PlaceholderPanel,
    'backtest-is-oos': PlaceholderPanel,
    'backtest-period-stability': PlaceholderPanel,
    'backtest-baseline': PlaceholderPanel,
    'backtest-sizing-settings': PlaceholderPanel,
    'backtest-sizing-equity': PlaceholderPanel,
    'backtest-not-taken': PlaceholderPanel,
    'journal-edge-rating': PlaceholderPanel,
    'journal-discipline': PlaceholderPanel,
    'journal-calendar': PlaceholderPanel,
    'journal-daily-pnl': PlaceholderPanel,
    'journal-day-metrics': PlaceholderPanel,
    'journal-day-trades': PlaceholderPanel,
    'journal-day-plans': PlaceholderPanel,
    'journal-day-pnl': PlaceholderPanel,
    'journal-recap': PlaceholderPanel,
    'journal-trades': PlaceholderPanel,
    'journal-trade-detail': PlaceholderPanel,
    'journal-annotation': PlaceholderPanel,
    'journal-plans': PlaceholderPanel,
    'journal-untaken-plans': PlaceholderPanel,
    'journal-exit-view': PlaceholderPanel,
    'journal-set-and-forget': PlaceholderPanel,
    'journal-mistakes': PlaceholderPanel,
    'journal-context-breakdown': PlaceholderPanel,
    'journal-context-scatter': PlaceholderPanel,
    'journal-population': PlaceholderPanel,
    'journal-backtest-live': PlaceholderPanel,
    'journal-lessons': PlaceholderPanel,
    'journal-ask': PlaceholderPanel,
    ...overrides,
  }
}

/** Dashboard registry — chart, scanner, key-stats, watchlist, news, ticker-stats are real. */
export const DASHBOARD_REGISTRY = createComponentRegistry({
  chart: SyncedChartGrid as PanelComponent,
  scanner: ScannerPanel,
  'multi-scanner': MultiScannerPanel,
  'key-stats': KeyStatsPanel,
  watchlist: WatchlistPanel,
  news: NewsPanel,
  'ticker-stats': TickerStatsPanel,
  calendar: CalendarPanel,
  notebook: NotebookPanel,
  'alert-log': AlertLogPanel,
  'multi-chart': MultiChartPanel,
  'sector-heatmap': HeatmapPanel,
  'theme-tracker': ThemeTrackerPanel,
  'market-overwatch': MarketOverwatchPanel,
  'scanner-parity': ScannerParityPanel,
  // EdgeScript editor panel, registered only behind the build gate (Task 2.10) and
  // shown only with `edgescript-release`; the lazy wrapper repeats the build gate
  // inline so the editor chunk stays out of user builds.
  ...(EDGESCRIPT_ENABLED ? { 'script-editor': ScriptEditorPanel } : {}),
  // Price Alerts only behind the edge-realtime build gate; a saved copy on a
  // gate-off build renders the placeholder.
  ...(REALTIME_WEBSOCKET_ENABLED
    ? { 'price-alerts': PriceAlertsPanel, 'alert-windows': AlertWindowsPanel }
    : {}),
})

/** History registry — real panels registered as they are built. */
export const HISTORY_REGISTRY = createComponentRegistry({
  'history-records': HistoryRecordsPanel,
  'history-stats': HistoryStatsPanel,
  'history-time-series': HistoryTimeSeriesPanel,
  'history-scatter': HistoryScatterPanel,
  'history-bar': HistoryBarPanel,
  'history-bar-category': HistoryBarCategoryPanel,
  'history-donut-category': HistoryDonutCategoryPanel,
  'history-chart': HistoryChartPanel,
  'history-news': HistoryNewsPanel,
  'history-comparison': HistoryComparisonPanel,
  'history-intraday': HistoryIntradayPanel,
  'history-calendar': HistoryCalendarPanel,
  'history-multi-chart': HistoryChartGridPanel,
  'history-sankey': HistorySankeyPanel,
  'history-scorecard': HistoryScorecardPanel,
  'history-heatmap': HistoryHeatmapPanel,
  'history-time-distribution': TimeDistributionPanel,
  'tags-explorer': TagExplorerPanel,
})

/**
 * Backtest report registry — thin adapter panels that pull data from
 * BacktestResultsContext and delegate rendering to the existing views in
 * features/backtest/components/stats/.
 *
 * Mount this registry inside a WorkspaceMosaic that is wrapped by a
 * BacktestResultsProvider (scope: 'backtest-report').
 */
export const BACKTEST_REPORT_REGISTRY = createComponentRegistry({
  'backtest-metrics': BacktestMetricsPanel,
  'backtest-overview': BacktestOverviewPanel,
  'backtest-performance': BacktestPerformancePanel,
  'backtest-trades': BacktestTradesPanel,
  'backtest-calendar': BacktestCalendarPanel,
  'backtest-distributions': BacktestDistributionsPanel,
  'backtest-buckets': BacktestBucketsPanel,
  'backtest-symbols': BacktestSymbolsPanel,
  'backtest-risk': BacktestRiskPanel,
  'backtest-wins-losses': BacktestWinsLossesPanel,
  'backtest-streaks': BacktestStreaksPanel,
  'backtest-trade-chart': BacktestTradeChartPanel,
  'backtest-trade-fills': BacktestTradeFillsPanel,
  'backtest-daily-pnl': BacktestDailyPnlPanel,
  'backtest-win-rate': BacktestWinRatePanel,
  'backtest-monthly': BacktestMonthlyPanel,
  'backtest-monthly-table': BacktestMonthlyTablePanel,
  'backtest-hold-times': BacktestHoldTimesPanel,
  'backtest-dist-hour': BacktestDistHourPanel,
  'backtest-dist-day': BacktestDistDayPanel,
  'backtest-dist-duration': BacktestDistDurationPanel,
  'backtest-return-histogram': BacktestReturnHistogramPanel,
  'backtest-day-metrics': BacktestDayMetricsPanel,
  'backtest-rolling-windows': BacktestRollingWindowsPanel,
  'backtest-general-metrics': BacktestGeneralMetricsPanel,
  'backtest-equity-curve': BacktestEquityCurvePanel,
  'backtest-strategy-rollup': BacktestStrategyRollupPanel,
  // ── Run-workspace panels (per-execution; shared registry with report scope) ─
  'backtest-run-trades': BacktestRunTradesPanel,
  'backtest-chart-grid': BacktestChartGridPanel,
  'backtest-filter-lab': BacktestFilterLabPanel,
  'backtest-field-explorer': BacktestFieldExplorerPanel,
  'backtest-monte-carlo': BacktestMonteCarloPanel,
  'backtest-monte-carlo-bands': BacktestMonteCarloBandsPanel,
  'backtest-is-oos': BacktestIsOosPanel,
  'backtest-period-stability': BacktestPeriodStabilityPanel,
  'backtest-baseline': BacktestBaselinePanel,
  'backtest-sizing-settings': BacktestSizingSettingsPanel,
  'backtest-sizing-equity': BacktestSizingEquityPanel,
  'backtest-not-taken': BacktestNotTakenPanel,
})

/**
 * Journal registry: the Journal's own tiles plus the backtest report tiles it
 * reuses. Mount inside a JournalProvider, which fills both the Journal context
 * and BacktestResultsContext from the trader's fills.
 */
export const JOURNAL_REGISTRY = createComponentRegistry({
  'backtest-equity-curve': BacktestEquityCurvePanel,
  'backtest-general-metrics': BacktestGeneralMetricsPanel,
  'backtest-monthly': BacktestMonthlyPanel,
  'backtest-monthly-table': BacktestMonthlyTablePanel,
  'backtest-rolling-windows': BacktestRollingWindowsPanel,
  'backtest-win-rate': BacktestWinRatePanel,
  'backtest-streaks': BacktestStreaksPanel,
  'backtest-dist-hour': BacktestDistHourPanel,
  'backtest-dist-day': BacktestDistDayPanel,
  'backtest-dist-duration': BacktestDistDurationPanel,
  'backtest-buckets': BacktestBucketsPanel,
  'backtest-return-histogram': BacktestReturnHistogramPanel,
  'backtest-symbols': BacktestSymbolsPanel,
  'backtest-wins-losses': BacktestWinsLossesPanel,
  'backtest-hold-times': BacktestHoldTimesPanel,
  'backtest-trade-chart': BacktestTradeChartPanel,
  'backtest-trade-fills': BacktestTradeFillsPanel,
  'journal-edge-rating': JournalEdgeRatingPanel,
  'journal-discipline': JournalDisciplinePanel,
  'journal-calendar': JournalCalendarPanel,
  'journal-daily-pnl': JournalDailyPnlPanel,
  'journal-day-metrics': JournalDayMetricsPanel,
  'journal-day-trades': JournalDayTradesPanel,
  'journal-day-plans': JournalDayPlansPanel,
  'journal-day-pnl': JournalDayPnlPanel,
  'journal-recap': JournalRecapPanel,
  'journal-trades': JournalTradesPanel,
  'journal-trade-detail': JournalTradeDetailPanel,
  'journal-annotation': JournalAnnotationPanel,
  'journal-plans': JournalPlansPanel,
  'journal-untaken-plans': JournalUntakenPlansPanel,
  'journal-exit-view': JournalExitViewPanel,
  'journal-set-and-forget': JournalSetAndForgetPanel,
  'journal-mistakes': JournalMistakesPanel,
  'journal-context-breakdown': JournalContextBreakdownPanel,
  'journal-context-scatter': JournalContextScatterPanel,
  'journal-population': JournalPopulationPanel,
  'journal-backtest-live': JournalBacktestLivePanel,
  'journal-lessons': JournalLessonsPanel,
  'journal-ask': JournalAskPanel,
})
